import { motion } from "framer-motion";
import { Handshake, ClipboardCheck, Globe } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const ICONS = [Handshake, ClipboardCheck, Globe];

export default function Collaboration() {
    const { t } = useT();
    const modes = t.collaboration.modes.map((m, i) => ({
        ...m,
        Icon: ICONS[i] || Handshake,
    }));

    return (
        <section
            id="collaboration"
            data-testid="collaboration-section"
            className="relative py-24 md:py-32 border-t border-[rgba(201,169,110,0.10)]"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-12">
                <p className="text-[11px] uppercase tracking-[0.14em] text-[#C9A96E] font-medium mb-6">
                    {t.collaboration.label}
                </p>
                <h2 className="font-display text-[28px] md:text-[42px] font-medium leading-[1.1] tracking-[-0.02em] max-w-[820px] mb-16">
                    {t.collaboration.headlineBefore}{" "}
                    <span className="text-[#C9A96E]">
                        {t.collaboration.headlineAccent}
                    </span>{" "}
                    {t.collaboration.headlineAfter}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {modes.map((m, i) => (
                        <motion.div
                            key={m.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{
                                duration: 0.6,
                                delay: i * 0.1,
                                ease: "easeOut",
                            }}
                            data-testid={`collaboration-card-${i + 1}`}
                            className="group p-8 md:p-10 border border-[rgba(201,169,110,0.15)] rounded-[4px] hover:border-[rgba(201,169,110,0.35)] transition-colors"
                        >
                            <div className="mb-8 text-[#C9A96E]">
                                <m.Icon
                                    size={28}
                                    strokeWidth={1.25}
                                    aria-hidden="true"
                                />
                            </div>
                            <h3 className="font-display text-[22px] md:text-[24px] font-medium leading-[1.25] tracking-[-0.01em] mb-4 text-[#E8E6DF]">
                                {m.title}
                            </h3>
                            <p className="text-[15px] leading-[1.7] text-[rgba(232,230,223,0.6)]">
                                {m.body}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    data-testid="collaboration-stats"
                    className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 border-y border-[rgba(201,169,110,0.15)] py-12 md:py-16"
                >
                    <div>
                        <p className="font-display text-[44px] md:text-[64px] font-medium leading-none tracking-[-0.02em] text-[#E8E6DF]">
                            {t.collaboration.statsACount}
                            <span className="text-[#C9A96E]">+</span>
                        </p>
                        <p className="mt-3 text-[11px] uppercase tracking-[0.14em] text-[rgba(232,230,223,0.55)]">
                            {t.collaboration.statsALabel}
                        </p>
                    </div>
                    <div>
                        <p className="font-display text-[44px] md:text-[64px] font-medium leading-none tracking-[-0.02em] text-[#E8E6DF]">
                            {t.collaboration.statsBCount}
                            <span className="text-[#C9A96E]">+</span>
                        </p>
                        <p className="mt-3 text-[11px] uppercase tracking-[0.14em] text-[rgba(232,230,223,0.55)]">
                            {t.collaboration.statsBLabel}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
